import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Card, CardBody } from "reactstrap";
import DataTable from "react-data-table-component";
import { useLayoutMode } from "../../../hooks/useLayoutMode";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../../i18/config";
import { getMainUsers, getUserGroups, removeUserGroup, saveUserGroup } from "../../../api";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { getCurrentCabinet } from "../../../helpers/functions";
import Loader from "../../../components/Loader";
import { UserGroup } from "../../Workflow/types";

interface CabinetUser {
    uuid: string;
    name?: string;
    email?: string;
}

interface GroupForm {
    uuid?: string;
    name: string;
    code: string;
    description: string;
    members: string[];
}

/**
 * Settings → Groups: named sets of cabinet users, assignable like a role
 * (workflow approvals first). Admins only; the cabinet enforces it.
 */
const UserGroupIndex = () => {
    const { t } = useTranslation(['workflow', 'menu']);
    const layoutMode = useLayoutMode();
    const [items, setItems] = useState<UserGroup[] | null>(null);
    const [users, setUsers] = useState<CabinetUser[]>([]);
    const [form, setForm] = useState<GroupForm | null>(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        getUserGroups().then((r: any) => setItems(r.items || [])).catch(() => setItems([]));
    }, []);

    useEffect(() => {
        load();
        getMainUsers({ page: 1, maxResults: 500, cabinetUuid: (getCurrentCabinet() || undefined)?.uuid }).then((r: any) => setUsers(r.items || [])).catch(() => setUsers([]));
    }, [load]);

    const userLabel = (uuid: string) => {
        const user = users.find((u) => u.uuid === uuid);

        return user ? (user.name || user.email || uuid) : uuid;
    };

    const fail = (e: any) => toast.error(e?.message || t('workflow:actionFailed'));

    const save = () => {
        if (!form || !form.name.trim()) {
            return;
        }
        setSaving(true);
        saveUserGroup({ uuid: form.uuid, name: form.name.trim(), code: form.code.trim() || null, description: form.description || null, members: form.members })
            .then(() => { setForm(null); load(); })
            .catch(fail)
            .finally(() => setSaving(false));
    };

    const remove = (group: UserGroup) => {
        if (window.confirm(t('workflow:confirmDeleteGroup', { name: group.name }))) {
            removeUserGroup(group.uuid).then(load).catch(fail);
        }
    };

    const toggleMember = (uuid: string) => form && setForm({
        ...form,
        members: form.members.includes(uuid) ? form.members.filter((m) => m !== uuid) : [...form.members, uuid],
    });

    const columns = [
        { name: t('workflow:name'), grow: 2, cell: (g: UserGroup) => <span><span className="fw-semibold">{g.name}</span>{g.code && <span className="text-muted small ms-2 font-monospace">{g.code}</span>}</span> },
        { name: t('workflow:members'), grow: 4, cell: (g: UserGroup) => <span className="small">{g.memberUuids.map(userLabel).join(', ') || '—'}</span> },
        {
            name: '',
            right: true,
            cell: (g: UserGroup) => (
                <div className="d-flex gap-1">
                    {g.actions?.canEdit &&
                        <button type="button" className="btn btn-sm btn-light" onClick={() => setForm({ uuid: g.uuid, name: g.name, code: g.code || '', description: g.description || '', members: [...g.memberUuids] })}>
                            <i className="ri-pencil-line" />
                        </button>
                    }
                    {g.actions?.canRemove && <button type="button" className="btn btn-sm btn-soft-danger" onClick={() => remove(g)}><i className="ri-delete-bin-line" /></button>}
                </div>
            ),
        },
    ];

    return (
        <>
            <BreadCrumb title={t('menu:settingsGroups')} pageTitle={t('menu:settings')} />
            <div className="d-flex mb-3">
                <button type="button" className="btn btn-success" onClick={() => setForm({ name: '', code: '', description: '', members: [] })}>
                    <i className="ri-add-line me-1" />{t('workflow:newGroup')}
                </button>
            </div>
            <Card>
                <CardBody>
                    <p className="text-muted small">{t('workflow:groupsIntro')}</p>
                    {items === null ? <Loader /> :
                        items.length === 0 ? <div className="text-center text-muted py-5">{t('workflow:noGroups')}</div> :
                            <DataTable theme={layoutMode} columns={columns as any} data={items} noHeader highlightOnHover />
                    }
                </CardBody>
            </Card>

            <Modal show={form !== null} onHide={() => setForm(null)} scrollable>
                <Modal.Header closeButton><Modal.Title>{form?.uuid ? t('workflow:editGroup') : t('workflow:newGroup')}</Modal.Title></Modal.Header>
                {form &&
                    <Modal.Body>
                        <div className="mb-3">
                            <label className="form-label small">{t('workflow:name')}</label>
                            <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label small">{t('workflow:code')}</label>
                            <input className="form-control font-monospace" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label small">{t('workflow:description')}</label>
                            <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <label className="form-label small">{t('workflow:members')}</label>
                        <div className="border rounded p-2" style={{ maxHeight: 280, overflowY: 'auto' }}>
                            {users.map((u) => (
                                <div key={u.uuid} className="form-check">
                                    <input id={'grp-' + u.uuid} className="form-check-input" type="checkbox" checked={form.members.includes(u.uuid)} onChange={() => toggleMember(u.uuid)} />
                                    <label className="form-check-label" htmlFor={'grp-' + u.uuid}>{u.name || u.email}{u.name && u.email ? <span className="text-muted small ms-1">{u.email}</span> : null}</label>
                                </div>
                            ))}
                            {users.length === 0 && <div className="text-muted small">{t('workflow:noUsers')}</div>}
                        </div>
                    </Modal.Body>
                }
                <Modal.Footer>
                    <button type="button" className="btn btn-light" onClick={() => setForm(null)}>{t('workflow:cancel')}</button>
                    <button type="button" className="btn btn-primary" disabled={saving || !form?.name.trim()} onClick={save}>{t('workflow:save')}</button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserGroupIndex;
